import type { ChatCycle, CycleResult } from "./types.js";

type BubbleKind = "user" | "assistant";

interface BuiltCycle {
  userRow: HTMLDivElement;
  toolRow: HTMLDivElement;
  resultRow: HTMLDivElement;
  assistantRow: HTMLDivElement;
}

function makeBubble(kind: BubbleKind, text: string): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "dls-row mcc-bubble mcc-bubble--" + kind;
  wrap.textContent = text;
  return wrap;
}

function makeToolChip(cycle: ChatCycle): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "dls-row mcc-tool-chip";

  const server = document.createElement("span");
  server.className = "mcc-tool-chip-server";
  server.textContent = cycle.server;
  wrap.appendChild(server);

  const arrow = document.createElement("span");
  arrow.className = "mcc-tool-chip-arrow";
  arrow.textContent = "›";
  wrap.appendChild(arrow);

  const call = document.createElement("span");
  call.className = "mcc-tool-chip-call";
  call.appendChild(document.createTextNode(cycle.tool + "("));
  cycle.args.forEach((arg, i) => {
    if (i > 0) call.appendChild(document.createTextNode(", "));
    call.appendChild(document.createTextNode(arg.k + "="));
    const v = document.createElement("span");
    v.className = "mcc-tool-chip-arg";
    v.textContent = arg.v;
    call.appendChild(v);
  });
  call.appendChild(document.createTextNode(")"));
  wrap.appendChild(call);

  const status = document.createElement("span");
  status.className = "mcc-tool-chip-status";
  const dot = document.createElement("span");
  dot.className = "dls-search-dot";
  dot.setAttribute("aria-hidden", "true");
  status.appendChild(dot);
  wrap.appendChild(status);

  return wrap;
}

const CHECK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check"><polyline points="20 6 9 17 4 12"></polyline></svg>';

function markToolDone(toolRow: HTMLElement): void {
  const status = toolRow.querySelector(".mcc-tool-chip-status");
  if (!status) return;
  status.innerHTML = CHECK_SVG;
}

function makeResultCard(result: CycleResult): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "dls-row mcc-result-card";

  result.rows.forEach((r) => {
    const row = document.createElement("div");
    row.className = "mcc-result-row";

    const acc = document.createElement("span");
    acc.className = "mcc-result-accession";
    acc.textContent = r.accession;
    row.appendChild(acc);

    const title = document.createElement("span");
    title.className = "mcc-result-title";
    title.textContent = r.title;
    row.appendChild(title);

    if (result.kind === "bars") {
      // narrow to BarResultRow via the result discriminator
      const barRow = r as { cases: number; controls: number };
      const total = barRow.cases + barRow.controls;
      const caseW = Math.max(8, Math.round((barRow.cases / total) * 80));
      const ctrlW = Math.max(8, Math.round((barRow.controls / total) * 80));

      const bar = document.createElement("span");
      bar.className = "mcc-result-bar";
      const cs = document.createElement("span");
      cs.className = "mcc-result-bar-case";
      cs.style.width = caseW + "px";
      cs.title = barRow.cases + " cases";
      bar.appendChild(cs);
      const cl = document.createElement("span");
      cl.className = "mcc-result-bar-ctrl";
      cl.style.width = ctrlW + "px";
      cl.title = barRow.controls + " controls";
      bar.appendChild(cl);
      row.appendChild(bar);

      const meta = document.createElement("span");
      meta.className = "mcc-result-meta";
      meta.textContent = barRow.cases + " / " + barRow.controls;
      row.appendChild(meta);
    } else {
      const metaRow = r as { meta: string };
      const meta = document.createElement("span");
      meta.className = "mcc-result-meta";
      meta.textContent = metaRow.meta;
      row.appendChild(meta);
    }

    wrap.appendChild(row);
  });

  if (result.chip) {
    const chipRow = document.createElement("div");
    chipRow.className = "mcc-result-row";
    chipRow.style.justifyContent = "flex-end";
    const chip = document.createElement("span");
    chip.className = "mcc-result-chip";
    chip.textContent = result.chip;
    chipRow.appendChild(chip);
    wrap.appendChild(chipRow);
  }

  return wrap;
}

export function initMcpChatDemo(cycles: ChatCycle[]): void {
  const root = document.getElementById("mcp-chat-demo");
  if (!root) return;
  const transcriptEl = root.querySelector<HTMLElement>("[data-mcc-transcript]");
  const serverEl = root.querySelector<HTMLElement>("[data-mcc-server]");
  if (!transcriptEl || !serverEl) return;
  if (!cycles.length) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const buildCycle = (cycle: ChatCycle): BuiltCycle => {
    serverEl.textContent = cycle.server;
    transcriptEl.innerHTML = "";
    const userRow = makeBubble("user", cycle.user);
    const toolRow = makeToolChip(cycle);
    const resultRow = makeResultCard(cycle.result);
    const assistantRow = makeBubble("assistant", cycle.assistant);
    transcriptEl.appendChild(userRow);
    transcriptEl.appendChild(toolRow);
    transcriptEl.appendChild(resultRow);
    transcriptEl.appendChild(assistantRow);
    return { userRow, toolRow, resultRow, assistantRow };
  };

  // Static fallback for reduced motion: first cycle, all rows visible, no cycling.
  if (reduceMotion) {
    const first = cycles[0];
    if (!first) return;
    const rows = buildCycle(first);
    Object.values(rows).forEach((r) => r.classList.add("is-in"));
    markToolDone(rows.toolRow);
    return;
  }

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  let cancelled = false;

  const playCycle = async (cycle: ChatCycle) => {
    const { userRow, toolRow, resultRow, assistantRow } = buildCycle(cycle);
    // Force layout before adding .is-in so the transition fires.
    void transcriptEl.offsetHeight;

    userRow.classList.add("is-in");
    await sleep(620);
    if (cancelled) return;
    toolRow.classList.add("is-in");
    await sleep(1080);
    if (cancelled) return;
    resultRow.classList.add("is-in");
    markToolDone(toolRow);
    await sleep(900);
    if (cancelled) return;
    assistantRow.classList.add("is-in");
    await sleep(3600);
  };

  const fadeAll = async () => {
    const rows = Array.from(transcriptEl.querySelectorAll(".dls-row"));
    rows.forEach((r) => {
      r.classList.remove("is-in");
      r.classList.add("is-out");
    });
    await sleep(260);
  };

  const runLoop = async () => {
    let idx = 0;
    while (!cancelled) {
      const cycle = cycles[idx];
      if (!cycle) return;
      await playCycle(cycle);
      if (cancelled) return;
      await fadeAll();
      if (cancelled) return;
      idx = (idx + 1) % cycles.length;
    }
  };

  let running = false;
  const start = () => {
    if (running) return;
    running = true;
    cancelled = false;
    void runLoop();
  };
  const stop = () => {
    cancelled = true;
    running = false;
  };

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) start();
          else stop();
        });
      },
      { threshold: 0.25 },
    );
    io.observe(root);
  } else {
    start();
  }
}
